import React, { useEffect, useMemo, useState } from "react";
import { X, ShieldAlert, Search } from "lucide-react";
import { api, formatAWG } from "@/lib/api";

// Legacy Order Audit — Phase 3 pre-launch (Feb 2026).
// STRICTLY READ-ONLY: renders GET /api/admin/orders-audit. No delete,
// archive, merge, convert or bulk-action controls exist here by design.
// Classifications are AUDIT FINDINGS computed at request time — not
// persisted order statuses.

const CLS_STYLES = {
  likely_real: "text-emerald-400 border-emerald-400/40 bg-emerald-400/10",
  test_dev: "text-sky-400 border-sky-400/40 bg-sky-400/10",
  incomplete_abandoned: "text-amber-400 border-amber-400/40 bg-amber-400/10",
  duplicate_suspected: "text-fuchsia-400 border-fuchsia-400/40 bg-fuchsia-400/10",
  legacy_schema: "text-[#A3A3A3] border-[#3A3A3A] bg-[#1A1A1A]",
  other: "text-[#A3A3A3] border-[#3A3A3A] bg-[#151515]",
};

const fmtDate = (v) => {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return String(v);
  }
};

const AuditPill = ({ cls, label }) => (
  <span
    data-testid={`audit-pill-${cls}`}
    className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${CLS_STYLES[cls] || CLS_STYLES.other}`}
  >
    {label}
  </span>
);

export const LegacyOrderAuditPanel = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [clsFilter, setClsFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [detailId, setDetailId] = useState(null);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    api.get("/admin/orders-audit")
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Failed to load audit"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!detailId) { setDetail(null); return; }
    api.get(`/admin/orders-audit/${detailId}`)
      .then((r) => setDetail(r.data))
      .catch(() => setDetail({ error: true }));
  }, [detailId]);

  const summary = data?.summary;
  const labels = summary?.classification_labels || {};

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (data?.orders || []).filter((o) => {
      if (clsFilter && o.classification !== clsFilter) return false;
      if (statusFilter && String(o.status) !== statusFilter) return false;
      if (q) {
        const hay = `${o.id} ${o.customer_name} ${o.customer_email}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [data, query, clsFilter, statusFilter]);

  const statuses = useMemo(
    () => Object.keys(summary?.status_distribution || {}),
    [summary]
  );

  if (loading) {
    return <div data-testid="order-audit-loading" className="text-sm text-[#A3A3A3] py-10 text-center">Building audit report…</div>;
  }
  if (error) {
    return <div data-testid="order-audit-error" className="text-sm text-red-400 py-10 text-center">{error}</div>;
  }

  return (
    <div data-testid="legacy-order-audit" className="space-y-6">
      {/* Read-only banner */}
      <div
        data-testid="order-audit-readonly-banner"
        className="flex items-start gap-3 rounded-xl border border-[#FF6600]/40 bg-[#FF6600]/5 px-4 py-3"
      >
        <ShieldAlert className="w-5 h-5 text-[#FF6600] shrink-0 mt-0.5" />
        <div className="text-sm text-[#B5B5B5] leading-relaxed">
          <span className="text-white font-semibold">Read-only audit.</span>{" "}
          {data?.audit_notice} Classifications are <span className="text-white font-semibold">audit findings</span> —
          they do not change any order's real status, and no delete / archive / merge / bulk actions exist in this view.
        </div>
      </div>

      {/* Summary counts */}
      <div data-testid="order-audit-summary" className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <div className="rounded-xl border border-[#262626] bg-[#141414] p-4">
          <div className="text-2xl font-black text-white" data-testid="audit-total-count">{summary.total_orders}</div>
          <div className="text-[10px] uppercase tracking-widest text-[#7A7A7A] mt-1">Total Records</div>
        </div>
        {Object.entries(summary.classification_counts).map(([cls, count]) => (
          <button
            key={cls}
            type="button"
            data-testid={`audit-summary-${cls}`}
            onClick={() => setClsFilter(clsFilter === cls ? "" : cls)}
            className={`rounded-xl border p-4 text-left transition-colors ${
              clsFilter === cls ? "border-[#FF6600] bg-[#1A1A1A]" : "border-[#262626] bg-[#141414] hover:border-[#3A3A3A]"
            }`}
          >
            <div className="text-2xl font-black text-white">{count}</div>
            <div className="text-[10px] uppercase tracking-widest text-[#7A7A7A] mt-1 leading-snug">
              {labels[cls]}
            </div>
          </button>
        ))}
      </div>

      {/* Distributions */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
        <div data-testid="audit-dist-dates" className="rounded-xl border border-[#262626] bg-[#141414] p-4">
          <div className="text-[10px] uppercase tracking-widest text-[#7A7A7A] mb-2">By Month</div>
          {Object.entries(summary.date_distribution_by_month).map(([m, c]) => (
            <div key={m} className="flex justify-between text-xs py-0.5">
              <span className="text-[#A3A3A3]">{m}</span>
              <span className="text-white font-semibold">{c}</span>
            </div>
          ))}
          <div className="mt-2 pt-2 border-t border-[#1F1F1F] text-[10px] text-[#7A7A7A]">
            Oldest: {fmtDate(summary.oldest_order)}<br />
            Newest: {fmtDate(summary.newest_order)}
          </div>
        </div>
        <div data-testid="audit-dist-status" className="rounded-xl border border-[#262626] bg-[#141414] p-4">
          <div className="text-[10px] uppercase tracking-widest text-[#7A7A7A] mb-2">Order Status</div>
          {Object.entries(summary.status_distribution).map(([k, c]) => (
            <div key={k} className="flex justify-between text-xs py-0.5">
              <span className="text-[#A3A3A3]">{k}</span>
              <span className="text-white font-semibold">{c}</span>
            </div>
          ))}
          <div className="text-[10px] uppercase tracking-widest text-[#7A7A7A] mt-3 mb-2">Payment Status</div>
          {Object.entries(summary.payment_status_distribution).map(([k, c]) => (
            <div key={k} className="flex justify-between text-xs py-0.5">
              <span className="text-[#A3A3A3]">{k}</span>
              <span className="text-white font-semibold">{c}</span>
            </div>
          ))}
        </div>
        <div data-testid="audit-dist-source" className="rounded-xl border border-[#262626] bg-[#141414] p-4">
          <div className="text-[10px] uppercase tracking-widest text-[#7A7A7A] mb-2">Line Kind / Source</div>
          {Object.entries(summary.line_kind_distribution).map(([k, c]) => (
            <div key={k} className="flex justify-between text-xs py-0.5">
              <span className="text-[#A3A3A3]">{k}</span>
              <span className="text-white font-semibold">{c}</span>
            </div>
          ))}
          <div className="text-[10px] uppercase tracking-widest text-[#7A7A7A] mt-3 mb-2">Schema Variants</div>
          {Object.entries(summary.schema_variants).slice(0, 6).map(([k, c]) => (
            <div key={k} className="flex justify-between text-xs py-0.5">
              <span className="text-[#A3A3A3] font-mono truncate max-w-[140px]" title={k}>{k}</span>
              <span className="text-white font-semibold">{c}</span>
            </div>
          ))}
        </div>
        <div data-testid="audit-dist-values" className="rounded-xl border border-[#262626] bg-[#141414] p-4">
          <div className="text-[10px] uppercase tracking-widest text-[#7A7A7A] mb-2">Values (excl. test/incomplete)</div>
          {Object.entries(summary.trustworthy_value_by_classification).map(([k, v]) => (
            <div key={k} className="flex justify-between text-xs py-0.5">
              <span className="text-[#A3A3A3]">{labels[k]}</span>
              <span className="text-white font-semibold">AWG {formatAWG(v)}</span>
            </div>
          ))}
          <div className="mt-2 pt-2 border-t border-[#1F1F1F] text-[10px] text-[#7A7A7A]">
            Suspected duplicate clusters: {summary.duplicate_cluster_count}
          </div>
        </div>
      </div>

      {/* Filters + search */}
      <div data-testid="order-audit-filters" className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#7A7A7A]" />
          <input
            data-testid="order-audit-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search order id, customer name or email…"
            className="w-full bg-[#1A1A1A] border border-[#262626] rounded-xl pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#FF6600]"
          />
        </div>
        <select
          data-testid="order-audit-classification-filter"
          value={clsFilter}
          onChange={(e) => setClsFilter(e.target.value)}
          className="bg-[#1A1A1A] border border-[#262626] rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#FF6600]"
        >
          <option value="">All classifications</option>
          {Object.entries(labels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select
          data-testid="order-audit-status-filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-[#1A1A1A] border border-[#262626] rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#FF6600]"
        >
          <option value="">All statuses</option>
          {statuses.map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
      </div>

      <div className="text-xs text-[#7A7A7A]" data-testid="order-audit-filter-count">
        Showing {filtered.length} of {summary.total_orders} records
      </div>

      {/* Table */}
      <div data-testid="order-audit-table" className="rounded-xl border border-[#262626] overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead>
            <tr className="bg-[#141414] text-[10px] uppercase tracking-widest text-[#7A7A7A] text-left">
              <th className="px-4 py-3">Order</th>
              <th className="px-4 py-3">Created</th>
              <th className="px-4 py-3">Customer</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Payment</th>
              <th className="px-4 py-3 text-right">Value</th>
              <th className="px-4 py-3 text-center">Lines</th>
              <th className="px-4 py-3">Audit Finding</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((o) => (
              <tr
                key={o.id}
                data-testid={`audit-row-${o.id}`}
                onClick={() => setDetailId(o.id)}
                className="border-t border-[#1F1F1F] hover:bg-[#151515] cursor-pointer"
              >
                <td className="px-4 py-2.5 font-mono text-xs text-[#A3A3A3]">{o.id}</td>
                <td className="px-4 py-2.5 text-xs text-[#A3A3A3] whitespace-nowrap">{fmtDate(o.created_at)}</td>
                <td className="px-4 py-2.5">
                  <div className="text-white text-xs font-semibold">{o.customer_name || "—"}</div>
                  <div className="text-[#7A7A7A] text-[11px]">{o.customer_email || "—"}</div>
                </td>
                <td className="px-4 py-2.5 text-xs">{o.status}</td>
                <td className="px-4 py-2.5 text-xs text-[#A3A3A3]">
                  {o.payment_status || "—"}{o.payment_mode ? ` · ${o.payment_mode}` : ""}
                </td>
                <td className="px-4 py-2.5 text-xs text-right whitespace-nowrap">
                  {o.subtotal != null ? `AWG ${formatAWG(o.subtotal)}` : "—"}
                </td>
                <td className="px-4 py-2.5 text-xs text-center">{o.line_count}</td>
                <td className="px-4 py-2.5"><AuditPill cls={o.classification} label={o.classification_label} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Detail drawer */}
      {detailId && (
        <div className="fixed inset-0 z-[100] bg-black/60 grid place-items-end" onClick={() => setDetailId(null)}>
          <aside
            data-testid="order-audit-detail"
            className="bg-[#0F0F0F] w-full max-w-2xl h-full border-l border-[#262626] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-[#0F0F0F] border-b border-[#262626] px-6 py-4 flex items-center justify-between z-10">
              <h3 className="font-display text-lg font-bold">
                Order Inspection <span className="font-mono text-sm text-[#A3A3A3]">{detailId}</span>
              </h3>
              <button data-testid="order-audit-detail-close" onClick={() => setDetailId(null)} className="w-9 h-9 rounded-full border border-[#262626] grid place-items-center">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-6 space-y-5">
              {!detail || detail.error ? (
                <div className="text-sm text-[#A3A3A3]">{detail?.error ? "Failed to load." : "Loading…"}</div>
              ) : (
                <>
                  <div className="flex items-center gap-3 flex-wrap">
                    <AuditPill cls={detail.classification} label={detail.classification_label} />
                    <span className="text-[11px] text-[#7A7A7A]">{detail.audit_notice}</span>
                  </div>
                  <div data-testid="order-audit-reasons" className="rounded-xl border border-[#262626] bg-[#141414] p-4">
                    <div className="text-[10px] uppercase tracking-widest text-[#7A7A7A] mb-2">Why this classification</div>
                    <ul className="list-disc list-inside text-xs text-[#B5B5B5] space-y-1">
                      {detail.reasons.map((r, i) => <li key={i}>{r}</li>)}
                    </ul>
                    <div className="mt-3 text-[11px] text-[#7A7A7A]">
                      Appears complete: <b className="text-white">{String(detail.flags.appears_complete)}</b> ·
                      Appears test: <b className="text-white">{String(detail.flags.appears_test)}</b> ·
                      Missing fields: <b className="text-white">{detail.flags.missing_critical_fields.join(", ") || "none"}</b>
                    </div>
                  </div>
                  {detail.line_references.length > 0 && (
                    <div data-testid="order-audit-references" className="rounded-xl border border-[#262626] bg-[#141414] p-4">
                      <div className="text-[10px] uppercase tracking-widest text-[#7A7A7A] mb-2">Referenced products still exist?</div>
                      {detail.line_references.map((r, i) => (
                        <div key={i} className="flex justify-between items-center text-xs py-1.5 border-b border-[#1F1F1F] last:border-0">
                          <div>
                            <div className="text-white">{r.display_name || r.item_code || "—"}</div>
                            <div className="text-[#7A7A7A] font-mono">{r.kind}{r.service_slug ? ` · ${r.service_slug}` : ""}</div>
                          </div>
                          <span className={r.product_exists === false ? "text-red-400" : "text-emerald-400"}>
                            {r.product_exists === null ? "n/a" : r.product_exists ? "exists" : "missing"}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div data-testid="order-audit-raw" className="rounded-xl border border-[#262626] bg-[#141414] p-4">
                    <div className="text-[10px] uppercase tracking-widest text-[#7A7A7A] mb-2">Raw record (read-only)</div>
                    <pre className="text-[11px] text-[#B5B5B5] whitespace-pre-wrap break-all max-h-[400px] overflow-y-auto font-mono">
                      {JSON.stringify(detail.order, null, 2)}
                    </pre>
                  </div>
                </>
              )}
            </div>
          </aside>
        </div>
      )}
    </div>
  );
};

export default LegacyOrderAuditPanel;
